import React, {useState, useEffect, useLayoutEffect, useMemo, useRef} from "react";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import {debounce, throttle} from "lodash";
import {dictionaryService} from "../api/services/dictionary.service";
import {KatoObject} from "../types/newPlanItem";

interface AutoSelectAddressServerProps {
    value: KatoObject | null;
    onChange: (newValue: KatoObject | null) => void;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
}

export const AutoSelectAddressServer: React.FC<AutoSelectAddressServerProps> = ({
                                                                                    value,
                                                                                    onChange,
                                                                                    label = "Адрес",
                                                                                    placeholder = "Введите адрес для поиска",
            disabled = false
                                                                                }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState<KatoObject[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [totalResults, setTotalResults] = useState(0);
    const [open, setOpen] = React.useState(false);

    // Ref для сохранения позиции скролла и предыдущей высоты контейнера
    const scrollPositionRef = useRef<number>(0);
    const previousScrollHeightRef = useRef<number>(0);
    const listboxRef = useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!open) {
            setPage(0);
            setOptions([]);
        } else {
            loadMore();
        }
    }, [open]);

    // Функция для получения адресов с сервера
    const fetchAddresses = async (
        searchText: string,
        pageNumber: number = 0,
        pageSize: number = 10
    ) => {
        setLoading(true);
        try {
            const response = await dictionaryService.searchKato(searchText, pageNumber, pageSize);
            const data = response.data;
            if (pageNumber === 0) {
                setOptions(data!.data);
            } else {
                setOptions((prev) => [...prev, ...data!.data]);
            }
            setTotalResults(data!.totalElements);
        } catch (error) {
            console.error("Ошибка при получении адресов:", error);
        } finally {
            setLoading(false);
        }
    };

    // Дебаунс для уменьшения количества запросов при вводе
    const debouncedFetch = useMemo(
        () =>
            debounce((text: string) => {
                setPage(0);
                fetchAddresses(text, 0, 10);
            }, 500),
        []
    );

    // Запуск запроса при изменении значения input
    useEffect(() => {
        if (inputValue.trim() === "") {
            setOptions([]);
            setTotalResults(0);
            return;
        }
        debouncedFetch(inputValue);
    }, [inputValue, debouncedFetch]);

    // Автоматический выбор, если найден ровно один адрес
    // useEffect(() => {
    //     if (options.length === 1 && !value) {
    //         onChange(options[0]);
    //     }
    // }, [options, value, onChange]);

    // Функция загрузки следующей страницы, если есть еще данные
    const loadMore = () => {

        // Сохраняем текущее значение scrollTop и высоту контейнера
        const nextPage = page + 1;
        setPage(nextPage);
        fetchAddresses(inputValue, nextPage);

    };

    // // Восстанавливаем положение скролла с учетом изменения высоты
    useLayoutEffect(() => {
        if (listboxRef.current) {
            // Если загружаются дополнительные данные (page > 0)
            if (page > 0) {
                const currentBox = listboxRef.current;
                const newScrollHeight = currentBox.scrollHeight;
                // Вычисляем разницу в высоте контейнера и корректируем scrollTop
                const diff = newScrollHeight - previousScrollHeightRef.current;
                currentBox.scrollTop = scrollPositionRef.current + diff;
            }
        }
    }, [options, page]);

    return (
        <Autocomplete
            disablePortal
            getOptionLabel={(option: KatoObject) => option.fullName}
            options={options}
            value={value}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onChange={(event, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            loading={loading}
            disabled={disabled}
            ListboxProps={{
                onScroll: (event: React.SyntheticEvent) => {
                    const listboxNode = event.currentTarget;
                    if (
                        Math.round(listboxNode.scrollTop + listboxNode.clientHeight) ===
                        listboxNode.scrollHeight
                    ) {
                        console.log('im here')
                        loadMore();
                    }
                },
            }} // передаём функцию handleScroll
            isOptionEqualToValue={(option, value) => option.id === value.id}
            size="small"
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress color="inherit" size={20}/>}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};